"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from "react";
import type { ViewKey, NavItem } from "./navTypes";
import { commonApiFetch } from "@/services/api/common-api";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useSearchParams, useRouter } from "next/navigation";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getHomeFeedRoute, getHomeLatestRoute, getMessagesBaseRoute, getWaveRoute, getWavesBaseRoute } from "@/helpers/navigation.helpers";
import type { HomeTab } from "@/components/home/useHomeTabs";
import { HOME_TAB_EVENT, getStoredHomeTab, setStoredHomeTab } from "@/components/home/useHomeTabs";
interface ViewContextType {
  activeView: ViewKey | null;
  hardBack: (v: ViewKey) => void;
  handleNavClick: (item: NavItem) => void;
  homeActiveTab: HomeTab;
  clearLastVisited: (type: "wave" | "dm") => void;
}
const ViewContext = createContext<ViewContextType | undefined>(undefined);
export const ViewProvider: React.FC<{
  readonly children: ReactNode;
}> = t0 => {
  const $ = _c(28);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    isApp
  } = useDeviceInfo();
  const [lastVisitedWave, setLastVisitedWave] = useState(null);
  const [lastVisitedDm, setLastVisitedDm] = useState(null);
  const [homeActiveTab, setHomeActiveTab] = useState(_temp);
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("wave");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const waveParam = t1;
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams.get("view");
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const viewParam = t2;
  const lastFetchedWaveIdRef = useRef(null);
  let t3;
  bb0: {
    if (waveParam) {
      t3 = null;
      break bb0;
    }
    t3 = viewParam ? viewParam as ViewKey : null;
  }
  const activeView = t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const {
        window: browserWindow
      } = globalThis as typeof globalThis & {
        window?: Window | undefined;
      };
      const handleTabEvent = event => {
        const detail = (event as CustomEvent<{
          tab?: HomeTab | undefined;
        }>).detail;
        const tab = detail.tab;
        if (tab !== "feed" && tab !== "latest") {
          return;
        }
        setHomeActiveTab(tab);
      };
      browserWindow.addEventListener(HOME_TAB_EVENT, handleTabEvent as EventListener);
      return () => {
        browserWindow.removeEventListener(HOME_TAB_EVENT, handleTabEvent as EventListener);
      };
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = targetWaveId => {
      commonApiFetch({
        endpoint: `/waves/${targetWaveId}`
      }).then(res => {
        if (res.chat.scope.group?.is_direct_message) {
          setLastVisitedDm(res.id);
        } else {
          setLastVisitedWave(res.id);
        }
      }).catch(_temp2).finally(() => {
        lastFetchedWaveIdRef.current = targetWaveId;
      });
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const fetchWaveDetails = t6;
  let t7;
  let t8;
  if ($[7] !== waveParam) {
    t7 = () => {
      if (waveParam && waveParam !== lastFetchedWaveIdRef.current) {
        fetchWaveDetails(waveParam);
      } else {
        if (!waveParam) {
          lastFetchedWaveIdRef.current = null;
        }
      }
    };
    t8 = [waveParam, fetchWaveDetails];
    $[7] = waveParam;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t9;
  if ($[10] !== isApp || $[11] !== lastVisitedDm || $[12] !== lastVisitedWave || $[13] !== router || $[14] !== searchParams) {
    t9 = item => {
      if (item.kind === "route") {
        if (item.name === "Stream") {
          setLastVisitedWave(null);
          setLastVisitedDm(null);
          setHomeActiveTab("feed");
          setStoredHomeTab("feed");
          router.push(getHomeFeedRoute());
        } else {
          if (item.name === "Home") {
            setHomeActiveTab("latest");
            setStoredHomeTab("latest");
            router.push(getHomeLatestRoute());
          } else {
            router.push(item.href);
          }
        }
      } else {
        if (item.viewKey === "waves") {
          const currentWaveId = searchParams.get("wave");
          if (currentWaveId) {
            setLastVisitedWave(null);
            router.push(getWavesBaseRoute(isApp));
          } else {
            if (lastVisitedWave) {
              router.push(getWaveRoute({
                waveId: lastVisitedWave,
                isDirectMessage: false,
                isApp
              }));
            } else {
              router.push(getWavesBaseRoute(isApp));
            }
          }
        } else {
          const currentWaveId_0 = searchParams.get("wave");
          if (currentWaveId_0) {
            setLastVisitedDm(null);
            router.push(getMessagesBaseRoute(isApp));
          } else {
            if (lastVisitedDm) {
              router.push(getWaveRoute({
                waveId: lastVisitedDm,
                isDirectMessage: true,
                isApp
              }));
            } else {
              router.push(getMessagesBaseRoute(isApp));
            }
          }
        }
      }
    };
    $[10] = isApp;
    $[11] = lastVisitedDm;
    $[12] = lastVisitedWave;
    $[13] = router;
    $[14] = searchParams;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleNavClick = t9;
  let t10;
  if ($[16] !== isApp || $[17] !== router) {
    t10 = v => {
      if (v === "messages") {
        setLastVisitedDm(null);
        router.push(getMessagesBaseRoute(isApp));
      } else {
        setLastVisitedWave(null);
        router.push(getWavesBaseRoute(isApp));
      }
    };
    $[16] = isApp;
    $[17] = router;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const hardBack = t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = type => {
      if (type === "wave") {
        setLastVisitedWave(null);
      } else {
        setLastVisitedDm(null);
      }
    };
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const clearLastVisited = t11;
  let t12;
  if ($[20] !== activeView || $[21] !== handleNavClick || $[22] !== hardBack || $[23] !== homeActiveTab) {
    t12 = {
      activeView,
      handleNavClick,
      hardBack,
      homeActiveTab,
      clearLastVisited
    };
    $[20] = activeView;
    $[21] = handleNavClick;
    $[22] = hardBack;
    $[23] = homeActiveTab;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const providerValue = t12;
  let t13;
  if ($[25] !== children || $[26] !== providerValue) {
    t13 = <ViewContext.Provider value={providerValue}>{children}</ViewContext.Provider>;
    $[25] = children;
    $[26] = providerValue;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
};
export const useViewContext = () => {
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error("useViewContext must be used within a ViewProvider");
  }
  return context;
};
function _temp() {
  return getStoredHomeTab();
}
function _temp2(error) {
  console.warn("Failed to fetch wave metadata", error);
}